import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { profileAPI, leadAPI, videoAPI, pdfAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Phone,
  Mail,
  Globe,
  MessageCircle,
  Download,
  Play,
  Send,
  User,
  ChevronRight,
  ExternalLink,
  Forward,
  X,
  CheckCircle,
  ArrowRight,
  RotateCcw,
  Briefcase,
  Image,
  FileText,
  Lock,
  Shield
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PROFILE_COLORS = {
  green: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', hex: '#10b981', gradient: 'from-emerald-500 to-teal-600' },
  blue: { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', hex: '#3b82f6', gradient: 'from-blue-500 to-indigo-600' },
  red: { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50', hex: '#ef4444', gradient: 'from-red-500 to-rose-600' },
  yellow: { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', hex: '#f59e0b', gradient: 'from-amber-500 to-orange-600' },
  purple: { bg: 'bg-purple-500', text: 'text-purple-600', light: 'bg-purple-50', hex: '#a855f7', gradient: 'from-purple-500 to-violet-600' },
  black: { bg: 'bg-slate-900', text: 'text-slate-900', light: 'bg-slate-100', hex: '#0f172a', gradient: 'from-slate-800 to-slate-900' },
};

const getStorageKey = (profileId) => `signa_wf_${profileId}`;
const getGateKey = (profileId) => `signa_gate_${profileId}`;

const PublicProfile = () => {
  const { profileId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Workflow
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasWorkflow, setHasWorkflow] = useState(false);

  // Lead form
  const [leadData, setLeadData] = useState({ name: '', phone: '', email: '' });
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  // Forward modal
  const [forwardModal, setForwardModal] = useState(null);
  const [forwardData, setForwardData] = useState({ recipient_email: '', sender_name: '', message: '' });
  const [forwardSending, setForwardSending] = useState(false);
  const [forwardSent, setForwardSent] = useState(false);

  // Lead gate (for PDF downloads)
  const [showLeadGate, setShowLeadGate] = useState(false);
  const [pendingDownloadPdfId, setPendingDownloadPdfId] = useState(null);
  const [gateUnlocked, setGateUnlocked] = useState(false);

  // Contact gating (for gated contact mode)
  const [contactsUnlocked, setContactsUnlocked] = useState(() => {
    return localStorage.getItem(`signa_contact_${profileId}`) === 'unlocked';
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileAPI.getPublic(profileId);
        setData(response.data);
        const wf = response.data.workflow || [];
        if (wf.length > 0) {
          setWorkflowSteps(wf);
          setHasWorkflow(true);
          const saved = localStorage.getItem(getStorageKey(profileId));
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.step < wf.length) setCurrentStep(parsed.step);
          }
        }
        // Check if gate already unlocked in session
        const gateStatus = localStorage.getItem(getGateKey(profileId));
        if (gateStatus === 'unlocked') setGateUnlocked(true);
      } catch (error) {
        console.error('Profile not found:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [profileId]);

  useEffect(() => {
    if (hasWorkflow && profileId) {
      localStorage.setItem(getStorageKey(profileId), JSON.stringify({ step: currentStep }));
    }
  }, [currentStep, hasWorkflow, profileId]);

  const trackStepEvent = useCallback(async (eventType, stepType, stepIndex) => {
    try {
      await fetch(`${API_BASE}/api/analytics/workflow-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: eventType, profile_id: profileId, step_type: stepType, step_id: `step_${stepIndex}` }),
      });
    } catch (e) { /* silent */ }
  }, [profileId]);

  useEffect(() => {
    if (hasWorkflow && workflowSteps[currentStep]) {
      trackStepEvent('workflow_step_view', workflowSteps[currentStep].type, currentStep);
    }
  }, [currentStep, hasWorkflow, workflowSteps, trackStepEvent]);

  const goNextStep = () => {
    if (currentStep < workflowSteps.length - 1) {
      trackStepEvent('workflow_step_complete', workflowSteps[currentStep].type, currentStep);
      setCurrentStep(currentStep + 1);
    }
  };

  const restartFunnel = () => {
    setCurrentStep(0);
    setLeadSubmitted(false);
    localStorage.removeItem(getStorageKey(profileId));
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    if (!leadData.name || !leadData.phone) { toast.error('Name and phone are required'); return; }
    setSubmittingLead(true);
    try {
      await leadAPI.create(profileId, leadData);
      trackStepEvent('lead_submission', 'lead_form', currentStep);
      setLeadSubmitted(true);
      toast.success("Thank you! We'll be in touch.");
      handleLeadSubmitAndUnlock();
      setLeadData({ name: '', phone: '', email: '' });
      setTimeout(() => {
        if (currentStep < workflowSteps.length - 1) {
          trackStepEvent('workflow_step_complete', 'lead_form', currentStep);
          setCurrentStep(currentStep + 1);
        }
      }, 1200);
    } catch (error) {
      toast.error('Failed to submit');
    } finally { setSubmittingLead(false); }
  };

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    if (!leadData.name || !leadData.phone) { toast.error('Name and phone are required'); return; }
    setSubmittingLead(true);
    try {
      await leadAPI.create(profileId, leadData);
      trackStepEvent('pdf_gate_completed', 'pdf_download', currentStep);
      setGateUnlocked(true);
      handleLeadSubmitAndUnlock();
      localStorage.setItem(getGateKey(profileId), 'unlocked');
      toast.success('Thank you! Download starting...');
      setLeadData({ name: '', phone: '', email: '' });
      setShowLeadGate(false);
      // Start download
      if (pendingDownloadPdfId) {
        window.open(pdfAPI.getDownloadUrl(pendingDownloadPdfId), '_blank');
        setPendingDownloadPdfId(null);
      }
    } catch (error) {
      toast.error('Failed to submit');
    } finally { setSubmittingLead(false); }
  };

  const handlePdfDownload = (pdfId, gating) => {
    trackStepEvent('pdf_download_click', 'pdf_download', currentStep);
    if (gating === 'lead_gate_before_download' && !gateUnlocked) {
      trackStepEvent('pdf_gate_shown', 'pdf_download', currentStep);
      setPendingDownloadPdfId(pdfId);
      setShowLeadGate(true);
    } else {
      window.open(pdfAPI.getDownloadUrl(pdfId), '_blank');
    }
  };

  const handleContactSave = () => {
    window.location.href = `${API_BASE}/api/vcf/${profileId}`;
  };

  const handleForwardVideo = async (e) => {
    e.preventDefault();
    if (!forwardData.recipient_email) { toast.error('Email required'); return; }
    setForwardSending(true);
    try {
      await videoAPI.forward(forwardModal, {
        video_id: forwardModal, recipient_email: forwardData.recipient_email,
        sender_name: forwardData.sender_name || null, message: forwardData.message || null,
      });
      setForwardSent(true);
      setTimeout(() => { setForwardModal(null); setForwardSent(false); setForwardData({ recipient_email: '', sender_name: '', message: '' }); }, 2000);
    } catch (error) { toast.error('Failed to forward'); }
    finally { setForwardSending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="spinner" /></div>;
  if (!data?.profile) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center"><h1 className="text-2xl font-bold text-slate-900 mb-2">Profile Not Found</h1><p className="text-slate-500">This profile doesn't exist or has been deactivated.</p></div>
    </div>
  );

  const { profile, organization, services, videos, photos, pdfs, contact_mode, contact_visibility, section_visibility } = data;
  const colorStyles = PROFILE_COLORS[profile.color] || PROFILE_COLORS.blue;
  const brandLogo = organization?.brand_logo_url || profile.profile_logo_url;
  const vis = contact_visibility || { phone: true, email: true, whatsapp: true, viber: true, vcf: true, website: true, services: true };
  const secVis = section_visibility || { header: true, contact: true, services: true, photos: true, videos: true, pdfs: true, lead_form: true, cta: true };
  const cMode = contact_mode || 'full';
  const isBroadcast = cMode === 'broadcast';

  // Effective visibility: combine toggle settings with gated/campaign/broadcast mode
  const effectiveVis = { ...vis };
  if (cMode === 'gated' && !contactsUnlocked) {
    effectiveVis.phone = false; effectiveVis.email = false; effectiveVis.whatsapp = false;
    effectiveVis.viber = false; effectiveVis.vcf = false; effectiveVis.website = false;
  }
  if (cMode === 'campaign' || isBroadcast) {
    effectiveVis.phone = false; effectiveVis.email = false; effectiveVis.whatsapp = false;
    effectiveVis.viber = false; effectiveVis.vcf = false; effectiveVis.website = false;
  }

  // Unlock contacts after lead form submission (for gated mode)
  const handleLeadSubmitAndUnlock = () => {
    if (cMode === 'gated') {
      setContactsUnlocked(true);
      localStorage.setItem(`signa_contact_${profileId}`, 'unlocked');
    }
  };

  // Shared props for step components
  const sharedProps = {
    profile, organization, services, videos, photos, pdfs, colorStyles, brandLogo, profileId,
    onContactSave: handleContactSave,
    onForwardVideo: (videoId) => { setForwardModal(videoId); setForwardSent(false); },
    onPdfDownload: handlePdfDownload,
    vis: effectiveVis,
    contactMode: cMode,
    contactsUnlocked,
    secVis,
    isBroadcast,
  };

  // No workflow → premium classic layout
  if (!hasWorkflow) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="public-profile">
        <PremiumClassicLayout {...sharedProps} />
        <ForwardVideoModal forwardModal={forwardModal} forwardData={forwardData} setForwardData={setForwardData} forwardSending={forwardSending} forwardSent={forwardSent} onSubmit={handleForwardVideo} onClose={() => setForwardModal(null)} />
        <LeadGateModal show={showLeadGate} leadData={leadData} setLeadData={setLeadData} submitting={submittingLead} onSubmit={handleGateSubmit} onClose={() => { setShowLeadGate(false); trackStepEvent('pdf_gate_abandoned', 'pdf_download', 0); }} colorStyles={colorStyles} />
      </div>
    );
  }

  // Workflow mode
  const step = workflowSteps[currentStep];
  const isLastStep = currentStep === workflowSteps.length - 1;
  const totalSteps = workflowSteps.length;
  const progressPct = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="workflow-funnel">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-40">
        <div className="h-1 bg-slate-200">
          <div className="h-full transition-all duration-500" style={{ width: `${progressPct}%`, backgroundColor: colorStyles.hex }} data-testid="workflow-progress-bar" />
        </div>
        <div className="flex items-center justify-between px-4 py-2 bg-white/90 backdrop-blur-md border-b border-slate-100">
          <span className="text-xs text-slate-500 font-medium" data-testid="step-indicator">Step {currentStep + 1} of {totalSteps}</span>
          <button onClick={restartFunnel} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600" data-testid="restart-funnel-btn">
            <RotateCcw className="w-3 h-3" /> Restart
          </button>
        </div>
      </div>

      <div className="pt-14 pb-28 max-w-lg mx-auto px-4">
        {step.type === 'landing_page' && <LandingPageStep {...sharedProps} />}
        {step.type === 'lead_form' && (
          <LeadFormStep settings={step.settings || {}} leadData={leadData} setLeadData={setLeadData} onSubmit={handleSubmitLead} submitting={submittingLead} submitted={leadSubmitted} colorStyles={colorStyles}
            skippable={step.settings?.skippable} onSkip={() => { trackStepEvent('workflow_step_skip', 'lead_form', currentStep); goNextStep(); }} />
        )}
        {step.type === 'video' && <VideoStep videos={videos} settings={step.settings || {}} colorStyles={colorStyles} onForwardVideo={sharedProps.onForwardVideo} />}
        {step.type === 'services' && <ServicesStep services={services} colorStyles={colorStyles} />}
        {step.type === 'photos' && <PhotosStep photos={photos} />}
        {step.type === 'cta' && <CTAStep settings={step.settings || {}} colorStyles={colorStyles} />}
        {step.type === 'thank_you' && <ThankYouStep settings={step.settings || {}} colorStyles={colorStyles} profile={profile} brandLogo={brandLogo} onRestart={restartFunnel} />}
        {step.type === 'pdf_download' && <PdfDownloadStep pdfs={pdfs} settings={step.settings || {}} colorStyles={colorStyles} onPdfDownload={handlePdfDownload} />}

        {/* Next Step */}
        {!isLastStep && !['lead_form', 'thank_you'].includes(step.type) && (
          <div className="mt-8">
            <Button onClick={goNextStep} className="w-full py-6 text-base font-medium rounded-xl shadow-lg" style={{ backgroundColor: colorStyles.hex }} data-testid="next-step-btn">
              Continue <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
        {step.type === 'lead_form' && leadSubmitted && !isLastStep && (
          <div className="mt-4">
            <Button onClick={goNextStep} className="w-full py-6 text-base font-medium rounded-xl" style={{ backgroundColor: colorStyles.hex }} data-testid="next-step-btn">
              Continue <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </div>

      <ForwardVideoModal forwardModal={forwardModal} forwardData={forwardData} setForwardData={setForwardData} forwardSending={forwardSending} forwardSent={forwardSent} onSubmit={handleForwardVideo} onClose={() => setForwardModal(null)} />
      <LeadGateModal show={showLeadGate} leadData={leadData} setLeadData={setLeadData} submitting={submittingLead} onSubmit={handleGateSubmit} onClose={() => { setShowLeadGate(false); trackStepEvent('pdf_gate_abandoned', 'pdf_download', currentStep); }} colorStyles={colorStyles} />
    </div>
  );
};

// ============== PREMIUM CLASSIC LAYOUT ==============
const PremiumClassicLayout = ({ profile, organization, services, videos, photos, pdfs, colorStyles, brandLogo, profileId, onContactSave, onForwardVideo, onPdfDownload, vis, contactMode, contactsUnlocked, secVis, isBroadcast }) => {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', phone: '', email: '' });
  const [localUnlocked, setLocalUnlocked] = useState(contactsUnlocked);

  const sv = secVis || { header: true, contact: true, services: true, photos: true, videos: true, pdfs: true, lead_form: true, cta: true };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    if (!leadData.name || !leadData.phone) { toast.error('Name and phone required'); return; }
    setSubmittingLead(true);
    try {
      await leadAPI.create(profileId, leadData);
      toast.success("We'll be in touch!");
      setLeadData({ name: '', phone: '', email: '' });
      setShowLeadForm(false);
      if (contactMode === 'gated') {
        setLocalUnlocked(true);
        localStorage.setItem(`signa_contact_${profileId}`, 'unlocked');
      }
    } catch (error) { toast.error('Failed to submit'); }
    finally { setSubmittingLead(false); }
  };

  // Re-evaluate effective vis for gated mode after local unlock
  const ev = { ...vis };
  if (contactMode === 'gated' && localUnlocked) {
    ev.phone = true; ev.email = true; ev.whatsapp = true; ev.viber = true; ev.vcf = true; ev.website = true;
  }

  // ===== BROADCAST LAYOUT =====
  if (isBroadcast) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 px-4" data-testid="broadcast-layout">
        {/* Primary Video */}
        {sv.videos && videos?.length > 0 && (
          <div className="w-full max-w-2xl mb-6">
            <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
              <video
                src={videos[0].url?.startsWith('/api') ? `${API_BASE}${videos[0].url}` : videos[0].url}
                controls
                className="w-full"
                data-testid="broadcast-video"
              />
            </div>
            {videos[0].title && <p className="text-center text-white/50 text-sm mt-3">{videos[0].title}</p>}
          </div>
        )}

        {/* Secondary Videos */}
        {sv.videos && videos?.length > 1 && (
          <div className="w-full max-w-2xl grid grid-cols-2 gap-3 mb-6">
            {videos.slice(1).map(v => (
              <div key={v.id} className="rounded-xl overflow-hidden shadow-lg bg-black/50">
                <video src={v.url?.startsWith('/api') ? `${API_BASE}${v.url}` : v.url} controls className="w-full aspect-video" />
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        {sv.cta && profile.website && (
          <a href={profile.website} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-lg shadow-xl transition-transform hover:scale-105"
            style={{ backgroundColor: colorStyles.hex }}
            data-testid="broadcast-cta"
          >
            Learn More <ExternalLink className="w-5 h-5" />
          </a>
        )}

        {/* Lead Form (optional in broadcast) */}
        {sv.lead_form && (
          <div className="w-full max-w-md mt-8">
            {!showLeadForm ? (
              <button onClick={() => setShowLeadForm(true)} className="w-full py-3 rounded-xl border border-white/20 text-white/60 text-sm hover:text-white hover:border-white/40 transition-colors" data-testid="broadcast-lead-btn">
                Get in Touch
              </button>
            ) : (
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5">
                <LeadFormInline leadData={leadData} setLeadData={setLeadData} onSubmit={handleSubmitLead} submitting={submittingLead} colorStyles={colorStyles} onCancel={() => setShowLeadForm(false)} dark />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ===== STANDARD LAYOUT =====
  const resolveUrl = (url) => url?.startsWith('/api') ? `${API_BASE}${url}` : url;

  return (
    <div>
      {/* Hero Section - only when header is visible */}
      {sv.header && (
        <div className={`relative bg-gradient-to-br ${colorStyles.gradient} pt-10 pb-36`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.1),transparent_70%)]" />
          <div className="relative max-w-lg mx-auto px-4 text-center">
            {brandLogo && <img src={resolveUrl(brandLogo)} alt="Logo" className="h-7 mx-auto mb-6 brightness-0 invert opacity-80" />}
            <div className="w-28 h-28 rounded-full mx-auto mb-5 ring-4 ring-white/30 shadow-xl overflow-hidden bg-white/20">
              {profile.profile_photo_url ? (
                <img src={resolveUrl(profile.profile_photo_url)} alt={profile.profile_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white text-4xl font-bold">{profile.profile_name?.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{profile.profile_name}</h1>
            {profile.title && <p className="text-white/80 text-lg">{profile.title}</p>}
            {organization?.brand_name && <p className="text-white/60 text-sm mt-1">{organization.brand_name}</p>}
            {profile.bio && <p className="text-white/70 mt-4 text-sm leading-relaxed max-w-sm mx-auto">{profile.bio}</p>}
          </div>
        </div>
      )}

      {/* Content Cards */}
      <div className={`relative z-10 max-w-lg mx-auto px-4 ${sv.header ? '-mt-20' : 'pt-6'} pb-8 space-y-4`}>
        {/* Action Card */}
        {sv.contact && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5 space-y-3">
            {ev.vcf && (
              <button onClick={onContactSave} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-medium shadow-md transition-transform hover:scale-[1.02]" style={{ backgroundColor: colorStyles.hex }} data-testid="save-contact-btn">
                <Download className="w-5 h-5" /> Save Contact
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              {ev.phone && profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 text-white text-sm font-medium" data-testid="call-btn">
                  <Phone className="w-4 h-4" /> Call
                </a>
              )}
              {ev.whatsapp && profile.phone && (
                <a href={`https://wa.me/${profile.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white text-sm font-medium" data-testid="whatsapp-btn">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {ev.email && profile.email && (
                <a href={`mailto:${profile.email}`} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-500 text-white text-sm font-medium" data-testid="email-btn">
                  <Mail className="w-4 h-4" /> Email
                </a>
              )}
              {ev.website && profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 text-slate-700 text-sm font-medium hover:border-slate-300" data-testid="website-btn">
                  <Globe className="w-4 h-4" /> Website
                </a>
              )}
            </div>
            {contactMode === 'gated' && !localUnlocked && (
              <p className="text-center text-xs text-slate-400 pt-1">Submit the contact form below to unlock all contact options</p>
            )}
          </div>
        )}

        {/* Services */}
        {sv.services && ev.services && services?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Services</h2>
            <ServicesList services={services} colorStyles={colorStyles} />
          </div>
        )}

        {/* Photos */}
        {sv.photos && photos?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Gallery</h2>
            <PhotoGallery photos={photos} />
          </div>
        )}

        {/* Videos */}
        {sv.videos && videos?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Videos</h2>
            <VideoList videos={videos} colorStyles={colorStyles} onForward={onForwardVideo} />
          </div>
        )}

        {/* PDFs / Downloads */}
        {sv.pdfs && pdfs?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Downloads</h2>
            <PdfList pdfs={pdfs} colorStyles={colorStyles} onDownload={onPdfDownload} />
          </div>
        )}

        {/* Lead Form */}
        {sv.lead_form && (
          <>
            {!showLeadForm ? (
              <button onClick={() => setShowLeadForm(true)} className="w-full py-4 rounded-2xl bg-slate-900 text-white font-medium flex items-center justify-center gap-2 shadow-lg hover:bg-slate-800 transition-colors" data-testid="show-lead-form-btn">
                <Send className="w-5 h-5" /> Get in Touch
              </button>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                <h3 className="text-base font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Let's Connect</h3>
                <LeadFormInline leadData={leadData} setLeadData={setLeadData} onSubmit={handleSubmitLead} submitting={submittingLead} colorStyles={colorStyles} onCancel={() => setShowLeadForm(false)} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============== STEP COMPONENTS ==============

const LandingPageStep = ({ profile, organization, services, videos, photos, pdfs, colorStyles, brandLogo, profileId, onContactSave, onForwardVideo, onPdfDownload, vis }) => {
  const resolveUrl = (url) => url?.startsWith('/api') ? `${API_BASE}${url}` : url;
  return (
  <div data-testid="step-landing-page">
    <div className="text-center pt-4 pb-6">
      {brandLogo && <img src={resolveUrl(brandLogo)} alt="Logo" className="h-7 mx-auto mb-4" />}
      <div className="w-24 h-24 rounded-full mx-auto mb-4 shadow-lg overflow-hidden" style={{ background: colorStyles.hex }}>
        {profile.profile_photo_url ? (
          <img src={resolveUrl(profile.profile_photo_url)} alt={profile.profile_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><span className="text-white text-3xl font-bold">{profile.profile_name?.charAt(0).toUpperCase()}</span></div>
        )}
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{profile.profile_name}</h1>
      {profile.title && <p className="text-slate-500">{profile.title}</p>}
      {organization?.brand_name && <p className="text-sm text-slate-400 mt-0.5">{organization.brand_name}</p>}
      {profile.bio && <p className="text-slate-600 mt-3 text-sm max-w-xs mx-auto">{profile.bio}</p>}
    </div>
    <div className="space-y-3 mb-6">
      {vis?.vcf !== false && (
        <button onClick={onContactSave} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-medium shadow-md" style={{ backgroundColor: colorStyles.hex }} data-testid="save-contact-btn">
          <Download className="w-5 h-5" /> Save Contact
        </button>
      )}
      <div className="grid grid-cols-2 gap-2">
        {vis?.phone !== false && profile.phone && <a href={`tel:${profile.phone}`} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 text-white text-sm font-medium" data-testid="call-btn"><Phone className="w-4 h-4" /> Call</a>}
        {vis?.whatsapp !== false && profile.phone && <a href={`https://wa.me/${profile.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white text-sm font-medium" data-testid="whatsapp-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
        {vis?.email !== false && profile.email && <a href={`mailto:${profile.email}`} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-500 text-white text-sm font-medium" data-testid="email-btn"><Mail className="w-4 h-4" /> Email</a>}
        {vis?.website !== false && profile.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 text-slate-700 text-sm font-medium" data-testid="website-btn"><Globe className="w-4 h-4" /> Website</a>}
      </div>
    </div>
    {vis?.services !== false && services?.length > 0 && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4"><h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Services</h2><ServicesList services={services} colorStyles={colorStyles} /></div>}
    {photos?.length > 0 && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4"><h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Gallery</h2><PhotoGallery photos={photos} /></div>}
    {videos?.length > 0 && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4"><h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Videos</h2><VideoList videos={videos} colorStyles={colorStyles} onForward={onForwardVideo} /></div>}
    {pdfs?.length > 0 && <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4"><h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Downloads</h2><PdfList pdfs={pdfs} colorStyles={colorStyles} onDownload={onPdfDownload} /></div>}
  </div>
  );
};

const LeadFormStep = ({ settings, leadData, setLeadData, onSubmit, submitting, submitted, colorStyles, skippable, onSkip }) => (
  <div className="pt-8" data-testid="step-lead-form">
    <div className="text-center mb-6">
      <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}>
        <Send className="w-6 h-6 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Let's Connect</h2>
      <p className="text-slate-500 mt-1">Share your info and we'll get back to you</p>
    </div>
    {submitted ? (
      <div className="text-center py-8" data-testid="lead-form-success">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-xl font-semibold text-slate-900">Thank you!</h3>
        <p className="text-slate-500 mt-1">We'll be in touch soon.</p>
      </div>
    ) : (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid="lead-form">
        <LeadFormInline leadData={leadData} setLeadData={setLeadData} onSubmit={onSubmit} submitting={submitting} colorStyles={colorStyles} buttonText={settings.button_text} />
        {skippable && <button type="button" onClick={onSkip} className="w-full text-center text-sm text-slate-400 hover:text-slate-600 py-3 mt-2" data-testid="skip-lead-form-btn">Skip for now</button>}
      </div>
    )}
  </div>
);

const VideoStep = ({ videos, settings, colorStyles, onForwardVideo }) => {
  const videoIndex = settings?.video_index ?? 0;
  const video = videos?.[videoIndex] || videos?.[0];
  return (
    <div className="pt-8" data-testid="step-video">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}><Play className="w-6 h-6 text-white" /></div>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Watch Video</h2>
      </div>
      {video ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <a href={video.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 hover:bg-slate-50 transition-colors" data-testid={`video-play-${video.id}`}>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: colorStyles.hex }}><Play className="w-7 h-7 text-white" /></div>
            <div className="flex-1"><p className="font-semibold text-slate-900 text-lg">{video.title || 'Watch Video'}</p><p className="text-sm text-slate-500">Tap to play</p></div>
            <ExternalLink className="w-5 h-5 text-slate-400" />
          </a>
          <div className="border-t border-slate-100 px-5 py-3">
            <button onClick={() => onForwardVideo(video.id)} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium" data-testid={`forward-video-${video.id}`}><Forward className="w-4 h-4" /> Forward to Email</button>
          </div>
        </div>
      ) : <p className="text-center text-slate-500">No video available</p>}
    </div>
  );
};

const ServicesStep = ({ services, colorStyles }) => (
  <div className="pt-8" data-testid="step-services">
    <div className="text-center mb-6"><div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}><Briefcase className="w-6 h-6 text-white" /></div><h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Services</h2></div>
    {services?.length > 0 ? <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5"><ServicesList services={services} colorStyles={colorStyles} /></div> : <p className="text-center text-slate-500">No services listed</p>}
  </div>
);

const PhotosStep = ({ photos }) => (
  <div className="pt-8" data-testid="step-photos">
    <div className="text-center mb-6"><div className="w-14 h-14 bg-indigo-500 rounded-2xl mx-auto mb-4 flex items-center justify-center"><Image className="w-6 h-6 text-white" /></div><h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Gallery</h2></div>
    {photos?.length > 0 ? <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5"><PhotoGallery photos={photos} /></div> : <p className="text-center text-slate-500">No photos</p>}
  </div>
);

const CTAStep = ({ settings, colorStyles }) => (
  <div className="pt-8 text-center" data-testid="step-cta">
    <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}><ExternalLink className="w-6 h-6 text-white" /></div>
    <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{settings.label || 'Learn More'}</h2>
    {settings.url && <a href={settings.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-white font-medium text-lg shadow-lg transition-transform hover:scale-105" style={{ backgroundColor: colorStyles.hex }} data-testid="cta-link">{settings.label || 'Learn More'} <ExternalLink className="w-5 h-5" /></a>}
  </div>
);

const ThankYouStep = ({ settings, colorStyles, profile, brandLogo, onRestart }) => (
  <div className="pt-12 text-center" data-testid="step-thank-you">
    {brandLogo && <img src={brandLogo} alt="Logo" className="h-7 mx-auto mb-6" />}
    <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}><CheckCircle className="w-10 h-10 text-white" /></div>
    <h2 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{settings.message || 'Thank you for visiting!'}</h2>
    <p className="text-slate-500 mb-8">{profile.profile_name}</p>
    <button onClick={onRestart} className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1 mx-auto" data-testid="restart-from-thankyou"><RotateCcw className="w-3 h-3" /> Start over</button>
  </div>
);

const PdfDownloadStep = ({ pdfs, settings, colorStyles, onPdfDownload }) => {
  const selectedIds = settings?.pdf_ids || [];
  const displayPdfs = selectedIds.length > 0 ? pdfs?.filter(p => selectedIds.includes(p.id)) : pdfs || [];
  const gating = settings?.gating || 'none';

  return (
    <div className="pt-8" data-testid="step-pdf-download">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}>
          <FileText className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{settings.label || 'Downloads'}</h2>
      </div>
      <div className="space-y-3">
        {displayPdfs.map((pdf) => (
          <button key={pdf.id} onClick={() => onPdfDownload(pdf.id, gating)} className="w-full bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4 hover:shadow-md transition-shadow text-left" data-testid={`download-pdf-${pdf.id}`}>
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0"><FileText className="w-6 h-6 text-red-600" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900">{pdf.title}</p>
              {pdf.description && <p className="text-sm text-slate-500 mt-0.5">{pdf.description}</p>}
            </div>
            <div className="flex items-center gap-1 text-sm font-medium shrink-0" style={{ color: colorStyles.hex }}>
              {gating === 'lead_gate_before_download' && <Lock className="w-4 h-4" />}
              <Download className="w-5 h-5" />
            </div>
          </button>
        ))}
        {displayPdfs.length === 0 && <p className="text-center text-slate-500">No downloads available</p>}
      </div>
    </div>
  );
};

// ============== SHARED SUB-COMPONENTS ==============

const ServicesList = ({ services, colorStyles }) => (
  <div className="space-y-3">
    {services.map((service) => (
      <div key={service.service_id} className={`p-4 rounded-xl ${colorStyles.light} border border-slate-100`}>
        <div className="flex items-center justify-between">
          <div><p className="font-medium text-slate-900">{service.service_name}</p>{service.description && <p className="text-sm text-slate-500 mt-0.5">{service.description}</p>}</div>
          {service.price && <span className="text-sm font-semibold text-slate-700">{service.price}</span>}
        </div>
        {service.cta_link && <a href={service.cta_link} target="_blank" rel="noopener noreferrer" className={`mt-2 inline-flex items-center text-sm font-medium ${colorStyles.text}`}>{service.cta_label || 'Learn more'} <ChevronRight className="w-4 h-4 ml-1" /></a>}
      </div>
    ))}
  </div>
);

const PhotoGallery = ({ photos }) => (
  <div className="grid grid-cols-3 gap-2">
    {photos.map((photo) => (
      <div key={photo.id} className="aspect-square rounded-xl overflow-hidden bg-slate-100">
        <img src={photo.url?.startsWith('/api') ? `${API_BASE}${photo.url}` : photo.url} alt={photo.title || 'Photo'} className="w-full h-full object-cover" />
      </div>
    ))}
  </div>
);

const VideoList = ({ videos, colorStyles, onForward }) => (
  <div className="space-y-3">
    {videos.map((video) => {
      const videoSrc = video.url?.startsWith('/api') ? `${API_BASE}${video.url}` : video.url;
      return (
        <div key={video.id} className="rounded-xl bg-slate-50 border border-slate-100 overflow-hidden">
          <div className="p-3">
            <video src={videoSrc} controls className="w-full rounded-lg" data-testid={`video-${video.id}`} />
          </div>
          <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700 truncate">{video.title || 'Video'}</p>
            <button onClick={() => onForward(video.id)} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium" data-testid={`forward-video-${video.id}`}><Forward className="w-3.5 h-3.5" /> Forward</button>
          </div>
        </div>
      );
    })}
  </div>
);

const PdfList = ({ pdfs, colorStyles, onDownload }) => (
  <div className="space-y-2">
    {pdfs.map((pdf) => (
      <button key={pdf.id} onClick={() => onDownload(pdf.id, 'none')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors text-left" data-testid={`download-pdf-${pdf.id}`}>
        <FileText className="w-8 h-8 text-red-500 shrink-0" />
        <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{pdf.title}</p>{pdf.description && <p className="text-xs text-slate-500">{pdf.description}</p>}</div>
        <Download className="w-5 h-5 shrink-0" style={{ color: colorStyles.hex }} />
      </button>
    ))}
  </div>
);

const LeadFormInline = ({ leadData, setLeadData, onSubmit, submitting, colorStyles, onCancel, buttonText, dark }) => (
  <form onSubmit={onSubmit} className="space-y-3">
    <div><Label className={dark ? 'text-white/70' : ''}>Name *</Label><div className="relative mt-1"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input className={`pl-10 ${dark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : ''}`} placeholder="Your name" value={leadData.name} onChange={(e) => setLeadData({ ...leadData, name: e.target.value })} required data-testid="lead-name-input" /></div></div>
    <div><Label className={dark ? 'text-white/70' : ''}>Phone *</Label><div className="relative mt-1"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input type="tel" className={`pl-10 ${dark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : ''}`} placeholder="Phone number" value={leadData.phone} onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })} required data-testid="lead-phone-input" /></div></div>
    <div><Label className={dark ? 'text-white/70' : ''}>Email (optional)</Label><div className="relative mt-1"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input type="email" className={`pl-10 ${dark ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40' : ''}`} placeholder="Email" value={leadData.email} onChange={(e) => setLeadData({ ...leadData, email: e.target.value })} data-testid="lead-email-input" /></div></div>
    <div className="flex gap-2">
      <Button type="submit" className="flex-1 py-5" style={{ backgroundColor: colorStyles.hex }} disabled={submitting} data-testid="submit-lead-btn">
        {submitting ? <div className="spinner w-5 h-5 border-2" /> : <>{buttonText || 'Submit'}</>}
      </Button>
      {onCancel && <Button type="button" variant={dark ? 'ghost' : 'outline'} className={dark ? 'text-white/60 hover:text-white' : ''} onClick={onCancel}>Cancel</Button>}
    </div>
  </form>
);

// ============== MODALS ==============

const ForwardVideoModal = ({ forwardModal, forwardData, setForwardData, forwardSending, forwardSent, onSubmit, onClose }) => {
  if (!forwardModal) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()} data-testid="forward-modal">
        {forwardSent ? (
          <div className="text-center py-6"><CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" /><h3 className="text-lg font-semibold text-slate-900">Sent!</h3><p className="text-sm text-slate-500 mt-1">The recipient will get the video link.</p></div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Forward Video</h3><button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button></div>
            <form onSubmit={onSubmit} className="space-y-3">
              <div><Label>Recipient Email *</Label><Input type="email" className="mt-1" placeholder="friend@example.com" value={forwardData.recipient_email} onChange={(e) => setForwardData({ ...forwardData, recipient_email: e.target.value })} required data-testid="forward-email-input" /></div>
              <div><Label>Your Name (optional)</Label><Input className="mt-1" placeholder="Your name" value={forwardData.sender_name} onChange={(e) => setForwardData({ ...forwardData, sender_name: e.target.value })} data-testid="forward-sender-input" /></div>
              <div><Label>Message (optional)</Label><Input className="mt-1" placeholder="Check out this video!" value={forwardData.message} onChange={(e) => setForwardData({ ...forwardData, message: e.target.value })} data-testid="forward-message-input" /></div>
              <Button type="submit" disabled={forwardSending} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="forward-submit-btn">{forwardSending ? <div className="spinner w-4 h-4 border-2" /> : <><Forward className="w-4 h-4 mr-2" /> Send</>}</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const LeadGateModal = ({ show, leadData, setLeadData, submitting, onSubmit, onClose, colorStyles }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()} data-testid="lead-gate-modal">
        <div className="text-center mb-5">
          <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: colorStyles.hex }}>
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Unlock Download</h3>
          <p className="text-sm text-slate-500 mt-1">Share your contact info to access this file</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div><Label>Name *</Label><div className="relative mt-1"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input className="pl-10" placeholder="Your name" value={leadData.name} onChange={(e) => setLeadData({ ...leadData, name: e.target.value })} required data-testid="gate-name-input" /></div></div>
          <div><Label>Phone *</Label><div className="relative mt-1"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input type="tel" className="pl-10" placeholder="Phone number" value={leadData.phone} onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })} required data-testid="gate-phone-input" /></div></div>
          <div><Label>Email (optional)</Label><div className="relative mt-1"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input type="email" className="pl-10" placeholder="Email" value={leadData.email} onChange={(e) => setLeadData({ ...leadData, email: e.target.value })} data-testid="gate-email-input" /></div></div>
          <Button type="submit" className="w-full py-5" style={{ backgroundColor: colorStyles.hex }} disabled={submitting} data-testid="gate-submit-btn">
            {submitting ? <div className="spinner w-5 h-5 border-2" /> : <><Lock className="w-4 h-4 mr-2" /> Unlock & Download</>}
          </Button>
          <button type="button" onClick={onClose} className="w-full text-center text-sm text-slate-400 hover:text-slate-600 py-2">Cancel</button>
        </form>
      </div>
    </div>
  );
};

export default PublicProfile;
